import type Highcharts from "highcharts";
import isInLegend from "@ui/Highcharts/lib/isInLegend";
import type { Period } from "@ui/Highcharts/hooks/usePeriod";
import {
  CLASSIC_FAILURE,
  FAULT_IMPACT_SERIE,
  MANUAL_FAILURE,
  MANUAL_FAILURE_IMPACT_SERIE,
  QUANTITY_UNEXPLAINED_GAP_ID,
  UNEXPLAINED_GAP_ID,
} from "../series/serieIds";
import { INTERNAL_GAP_ID } from "../series/getInternalGapSerie";
import { CONFIDENCE_AREA_ID } from "../series/getConfidenceAreaSerie";
import { QUANTITY_GAP_SERIE_ID } from "../../Diagnosis/Symptoms/getQuantityGapsSerie";
import { NO_FAILURES_SERIE_ID } from "../../Diagnosis/FailureEvolution/getNoFailureSeries";

export const getFailureDisplayName = (serie: { name?: string; custom?: unknown }) => {
  if (typeof serie.custom === "object" && serie.custom !== null) {
    const custom = serie.custom as { [key: string]: unknown };
    const failureDisplayName = custom["failureDisplayName"];
    if (typeof failureDisplayName === "string") return failureDisplayName;
  }
  return serie.name;
};

export const isFailureSerie = (serie: { id?: string | null }) =>
  Boolean(
    serie.id?.includes(CLASSIC_FAILURE) ||
      serie.id?.includes(MANUAL_FAILURE) ||
      serie.id?.includes(FAULT_IMPACT_SERIE) ||
      serie.id?.includes(MANUAL_FAILURE_IMPACT_SERIE),
  );

export const sortFailureGroupNames = (groupNames: string[], ungroupedLabel: string) =>
  [...groupNames].sort((a, b) => {
    if (a === ungroupedLabel) return 1;
    if (b === ungroupedLabel) return -1;
    return a.localeCompare(b);
  });

export const buildGroupedLegendSeries = <T extends Highcharts.SeriesOptionsType>({
  series,
  period,
  groupByFailureDisplayName,
  ungroupedLabel,
}: {
  series: T[];
  period?: Period;
  groupByFailureDisplayName?: Record<string, string>;
  ungroupedLabel: string;
}) => {
  if (!groupByFailureDisplayName) return series;

  const orderedSeries = series
    .filter(serie => isInLegend(serie, period))
    .sort((s1, s2) => {
      const s1Index = s1.legendIndex ?? 0;
      const s2Index = s2.legendIndex ?? 0;

      if (s1Index < s2Index) return -1;
      if (s1Index > s2Index) return 1;
      return 0;
    });

  const otherSeries = orderedSeries.filter(serie => !isFailureSerie(serie));
  const failureSeries = orderedSeries.filter(serie => isFailureSerie(serie));
  const grouped = new Map<string, T[]>();

  for (const serie of failureSeries) {
    const displayName = getFailureDisplayName(serie);
    if (!displayName) continue;
    const groupName = groupByFailureDisplayName[displayName] ?? ungroupedLabel;
    const list = grouped.get(groupName) ?? [];
    list.push(serie);
    grouped.set(groupName, list);
  }

  const groupOrder = sortFailureGroupNames(Array.from(grouped.keys()), ungroupedLabel);
  const groupedFailures = groupOrder.flatMap(groupName => {
    const groupSeries = grouped.get(groupName) ?? [];
    return [...groupSeries].sort((a, b) => {
      const nameA = getFailureDisplayName(a) ?? "";
      const nameB = getFailureDisplayName(b) ?? "";
      return nameA.localeCompare(nameB);
    });
  });

  return [...otherSeries, ...groupedFailures].map((serie, index) => ({
    ...serie,
    legendIndex: index,
  }));
};

export const getFailureGroupOrder = (
  groupByFailureDisplayName: Record<string, string> | undefined,
  ungroupedLabel: string,
) => {
  if (!groupByFailureDisplayName) return [];
  const names = new Set(Object.values(groupByFailureDisplayName));
  names.add(ungroupedLabel);
  return sortFailureGroupNames(Array.from(names), ungroupedLabel);
};

export const isOtherFailureLegendSerieId = (
  serieId: string,
  { includeNoFailures = false }: { includeNoFailures?: boolean } = {},
) => {
  const otherSerieIds = [
    INTERNAL_GAP_ID,
    UNEXPLAINED_GAP_ID,
    QUANTITY_UNEXPLAINED_GAP_ID,
    QUANTITY_GAP_SERIE_ID,
    CONFIDENCE_AREA_ID,
  ];

  if (includeNoFailures) {
    otherSerieIds.push(NO_FAILURES_SERIE_ID);
  }

  return otherSerieIds.includes(serieId);
};
